import type { CartOperation, Order } from '@planner/shared';
import type { ProductDataFile } from '@planner/shared';
import { apiBaseUrl, authDisabled } from './env.js';
import { msalInstance } from './msal.js';

export interface ListOrdersResult {
  orders: Order[];
  nextCursor?: string;
}

export interface ListOrdersFilters {
  region?: string;
  status?: string;
  limit?: number;
  cursor?: string;
}

/**
 * Central 401 handling: the session is gone (expired token, revoked account),
 * so clear the cached MSAL state and send the user back to the login page
 * instead of leaving each page to render a dead error message.
 */
async function handleUnauthorized(): Promise<never> {
  if (!authDisabled) {
    await msalInstance.clearCache().catch(() => undefined);
    window.location.assign('/login');
  }
  throw new Error('Your session has expired — please sign in again');
}

async function checkedResponse(response: Response): Promise<Response> {
  if (response.status === 401) {
    return handleUnauthorized();
  }
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed (${response.status})`);
  }
  return response;
}

async function request<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  await checkedResponse(response);
  return response.json() as Promise<T>;
}

export function listOrders(token: string, filters: ListOrdersFilters = {}) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  }
  const suffix = params.size > 0 ? `?${params.toString()}` : '';
  return request<ListOrdersResult>(token, `/admin/orders${suffix}`);
}

export function getOrder(token: string, reference: string) {
  return request<{ order: Order; cartOperations: CartOperation[] }>(
    token,
    `/admin/orders/${encodeURIComponent(reference)}`,
  );
}

export async function exportOrdersCsv(
  token: string,
  filters: Pick<ListOrdersFilters, 'region' | 'status'> = {},
): Promise<Blob> {
  const baseParams = new URLSearchParams({ paged: 'true' });
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') {
      baseParams.set(key, value);
    }
  }
  const chunks: string[] = [];
  const seenCursors = new Set<string>();
  let header: string | undefined;
  let cursor: string | undefined;
  let limit = 100;
  for (;;) {
    const params = new URLSearchParams(baseParams);
    params.set('limit', String(limit));
    if (cursor) params.set('cursor', cursor);
    const response = await fetch(`${apiBaseUrl}/admin/orders/export?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 413) {
      if (limit === 1) {
        throw new Error(
          "An order's Cart operation history is too large to export. Contact support.",
        );
      }
      limit = Math.max(1, Math.floor(limit / 2));
      continue;
    }
    await checkedResponse(response);
    const page: unknown = await response.json().catch(() => {
      throw new Error('The order export response was invalid.');
    });
    if (
      !page ||
      typeof page !== 'object' ||
      !('csv' in page) ||
      typeof page.csv !== 'string' ||
      ('nextCursor' in page &&
        (typeof page.nextCursor !== 'string' || page.nextCursor.length === 0))
    ) {
      throw new Error('The order export response was invalid.');
    }
    const lineBreak = page.csv.indexOf('\n');
    const pageHeader = lineBreak < 0 ? page.csv : page.csv.slice(0, lineBreak);
    const rows = lineBreak < 0 ? '' : page.csv.slice(lineBreak + 1);
    if (!pageHeader || (header !== undefined && header !== pageHeader)) {
      throw new Error('The order export columns changed between pages.');
    }
    if (header === undefined) {
      header = pageHeader;
      chunks.push(header);
    }
    if (rows) chunks.push('\n', rows);
    cursor =
      'nextCursor' in page && typeof page.nextCursor === 'string' ? page.nextCursor : undefined;
    if (cursor) {
      if (seenCursors.has(cursor)) throw new Error('The order export pagination cursor repeated.');
      seenCursors.add(cursor);
    } else {
      break;
    }
  }
  return new Blob(chunks, { type: 'text/csv' });
}

export function getProducts(token: string, region: string, feed: string) {
  return request<ProductDataFile>(token, `/admin/products/${region}/${feed}`);
}

export function triggerSync(token: string) {
  return request<{ triggered: boolean }>(token, '/admin/sync', { method: 'POST' });
}
